import {Component, ReactNode} from 'react';
import {findDOMNode} from 'react-dom';
import {tryCall} from '../../utils';
import {AnimatableElement, AnimationState, DurationProp, StateChangedCallback} from '../types';
import {getDurationFor} from '../utils';
import {AnimationTimer} from '../utils/animation-timer';

export type TransitionProps = {
	in: boolean;
	children: ReactNode;
	duration: DurationProp;
	animateOnMount?: boolean;
	onStateChanged?: StateChangedCallback;
	beforeStateChanged?: StateChangedCallback;
}

export class Transition extends Component<TransitionProps> {
	private currAnimationState: AnimationState;
	private prevAnimationState: AnimationState;
	private animationTimer: AnimationTimer = new AnimationTimer();

	constructor(props: TransitionProps) {
		super(props);
		this.currAnimationState = props.in ? 'enter' : 'initial';
	}

	public override componentDidMount() {
		const ref = this.domNode;
		if (ref) {
			this.notifyStateChanged(ref);
		}
		this.initTimer();
		this.handleInChange();
	}

	public override componentDidUpdate(prevProps: Readonly<TransitionProps>, prevState: Readonly<{}>, snapshot?: any) {
		if (prevProps.in !== this.props.in) {
			this.handleInChange();
		}
	}

	private get domNode(): AnimatableElement {
		const ref = findDOMNode(this);
		if (ref instanceof HTMLElement || ref instanceof SVGElement) {
			return ref;
		} else {
			return null;
		}
	}

	private initTimer(): void {
		this.animationTimer.onStart(() => {
			const ref = this.domNode;
			if (ref) {
				this.notifyStateWillChanged(ref);
			}
		}).onEnd(() => {
			const ref = this.domNode;
			if (ref) {
				this.prevAnimationState = this.currAnimationState;
				switch (this.currAnimationState) {
					case 'initial': {
						if(this.props.in) {
							this.currAnimationState = 'enter';
						} else {
							return;
						}
						break;
					}
					case 'exit': {
						this.notifyStateChanged(ref);
						this.currAnimationState = 'initial';
					}
				}
				this.notifyStateWillChanged(ref);
				this.notifyStateChanged(ref);
			}
		});
	}

	private handleInChange() {
		const currAnimationState = this.currAnimationState;
		const isInitializing = !this.prevAnimationState;
		if (this.props.in) {
			if(this.props.animateOnMount && currAnimationState === 'enter') {
				this.currAnimationState = 'initial';
			} else {
				this.currAnimationState = 'enter';
			}
		} else {
			if (isInitializing) {
				this.currAnimationState = 'initial';
			} else {
				this.currAnimationState = 'exit';
			}
		}
		this.prevAnimationState = currAnimationState;
		if (this.currAnimationState !== this.prevAnimationState || isInitializing) {
			const node = this.domNode;

			if (isInitializing && !this.props.animateOnMount) {
				this.notifyStateWillChanged(node);
				this.notifyStateChanged(node);
			} else {
				const duration = getDurationFor(this.currAnimationState, this.props.duration);
				this.animationTimer
					.cancel()
					.setDuration(duration)
					.start();
			}
		}
	}

	public override render() {
		return this.props.children;
	}

	public override componentWillUnmount() {
		this.animationTimer.destroy();
	}

	private notifyStateWillChanged(ref: AnimatableElement) {
		tryCall(this.props.beforeStateChanged, this.currAnimationState, ref);
	}

	private notifyStateChanged(ref: AnimatableElement): void {
		tryCall(this.props.onStateChanged, this.currAnimationState, ref);
	}
}
